/**
 * 372 (#1240): does an edit to a discount (a voucher) give more away, so that it must be approved again?
 *
 * One rule for the backend (which decides) and the admin (which warns before saving). It FAILS CLOSED: any change to a
 * field it does not know as a decrease or as text needs approval. The admin form sends every field on each save, so it
 * compares values, never presence; dates compare as instants.
 */
type Fields = Record<string, unknown>;

/** A date as an instant. 372 (review): compared exactly; the admin form keeps an unchanged day's original time. */
const t = (v: unknown): number | null =>
  v === undefined || v === null || v === '' ? null : new Date(v as string).getTime();
const n = (v: unknown): number | null => (v === undefined || v === null ? null : Number(v));
const ids = (v: unknown): string[] => (Array.isArray(v) ? v.map((x) => String(x)) : []);
const subset = (a: string[], b: string[]) => a.every((x) => b.includes(x));
const sameSet = (a: string[], b: string[]) => subset(a, b) && subset(b, a);

/** Fields that change wording only. */
const TEXT = new Set(['name', 'description', 'note']);

/** For each known field: [label, does `after` give more than `before`?]. Only called when the values differ. */
const RULES: Record<string, [string, (before: unknown, after: unknown) => boolean]> = {
  discount_type: ['type', () => true],
  discount_value: ['value', (b, a) => (n(a) ?? 0) > (n(b) ?? 0)],
  // No cap gives the most; a cap where there was none is a decrease.
  max_discount_minor: [
    'cap',
    (b, a) => n(a) === null || (n(b) !== null && (n(a) as number) > (n(b) as number)),
  ],
  valid_from: [
    'start',
    (b, a) => t(a) === null || (t(b) !== null && (t(a) as number) < (t(b) as number)),
  ],
  valid_until: [
    'end',
    (b, a) => t(a) === null || (t(b) !== null && (t(a) as number) > (t(b) as number)),
  ],
  is_active: ['switched on', (_b, a) => a === true],
  min_booking_amount_minor: ['minimum amount', (b, a) => (n(a) ?? 0) < (n(b) ?? 0)],
  total_redemptions_limit: [
    'total limit',
    (b, a) => n(a) === null || (n(b) !== null && (n(a) as number) > (n(b) as number)),
  ],
  usage_per_user: [
    'per-customer limit',
    (b, a) => n(a) === null || (n(b) !== null && (n(a) as number) > (n(b) as number)),
  ],
  non_combinable: ['combinable', (_b, a) => a === false],
  customer_id: ['customer', () => true],
  rental_window: [
    'rental window',
    (b, a) => {
      const bw = b as { from?: unknown; to?: unknown } | null | undefined;
      const aw = a as { from?: unknown; to?: unknown } | null | undefined;
      if (!aw) return true; // no window allows every rental
      if (!bw) return false; // a window where there was none narrows
      return (
        (t(aw.from) as number) < (t(bw.from) as number) ||
        (t(aw.to) as number) > (t(bw.to) as number)
      );
    },
  ],
};

/** What the schema stores when a field is absent (records made before a field existed carry none). */
const DEFAULTS: Record<string, unknown> = { non_combinable: false, is_active: true };

function differs(key: string, before: unknown, after: unknown): boolean {
  if (key in DEFAULTS) return (before ?? DEFAULTS[key]) !== (after ?? DEFAULTS[key]);
  if (key === 'car_ids' || key === 'car_model_ids') return !sameSet(ids(before), ids(after));
  if (key === 'valid_from' || key === 'valid_until') return t(before) !== t(after);
  if (key === 'rental_window') {
    const b = before as { from?: unknown; to?: unknown } | null | undefined;
    const a = after as { from?: unknown; to?: unknown } | null | undefined;
    return t(b?.from) !== t(a?.from) || t(b?.to) !== t(a?.to);
  }
  if (typeof before === 'number' || typeof after === 'number') return n(before) !== n(after);
  return String(before ?? '') !== String(after ?? '');
}

/** The car restriction is the two lists together: a car matches either. Both empty: every car. */
function carsWiden(before: Fields, after: Fields): boolean {
  const bc = ids(before.car_ids);
  const bm = ids(before.car_model_ids);
  const ac = 'car_ids' in after ? ids(after.car_ids) : bc;
  const am = 'car_model_ids' in after ? ids(after.car_model_ids) : bm;
  if (bc.length === 0 && bm.length === 0) return false; // every car before: nothing can widen it
  if (ac.length === 0 && am.length === 0) return true; // every car after
  return !subset(ac, bc) || !subset(am, bm);
}

export interface ApprovalDecision {
  needed: boolean;
  /** What gives more away, in words, for the record (e.g. `value`, `per-customer limit`). */
  changes: string[];
}

export function discountChangeNeedsApproval(before: Fields, patch: Fields): ApprovalDecision {
  const changes: string[] = [];
  let carsChecked = false;
  for (const [key, after] of Object.entries(patch)) {
    if (after === undefined || TEXT.has(key)) continue;
    if (!differs(key, before[key], after)) continue;
    if (key === 'car_ids' || key === 'car_model_ids') {
      if (!carsChecked && carsWiden(before, patch)) changes.push('cars');
      carsChecked = true;
      continue;
    }
    const rule = RULES[key];
    if (!rule) {
      changes.push(key); // unknown field: fail closed
      continue;
    }
    if (rule[1](before[key], after)) changes.push(rule[0]);
  }
  return { needed: changes.length > 0, changes };
}
